import React, { useRef, useState } from "react";
import SbTour from "./SbTour";
import buildTourSteps from "./buildTourSteps";
import useReadyTour from "./useReadyTour";

// Legs never contain greeting (Welcome / Finish) steps — those stay on the
// module's own page — so buildTourSteps' greeting handlers are never called.
const NO_GREETING = { icon: () => null, onPrimary: () => {}, onSecondary: () => {} };

/**
 * SbTourLeg — the part of a module's tour that a *different* page shows.
 *
 * A module tour that hops pages (see useTourStore's crossPageTourPhase) needs
 * every destination page to open its own steps once it has settled, track its
 * own current step, and hand the baton on when its steps run out. This wraps
 * that boilerplate (useReadyTour + buildTourSteps + SbTour) so a leg page only
 * declares its steps, targets and where to go next:
 *
 *   <SbTourLeg
 *     active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.ROLE_DETAIL && !isLoading}
 *     ready={!isLoading}
 *     steps={ROLE_DETAIL_TOUR_STEPS}
 *     resolveTarget={(step) => refs[step.key]?.current}
 *     onNext={() => { setCrossPageTourPhase(NEXT_PHASE); navigate(nextPage); }}
 *     onClose={() => { setCrossPageTourPhase(FINISH_PHASE); navigate(originPage); }}
 *   />
 *
 * The leg's last step reads "Next", not AntD's "Finish", because the tour
 * carries on elsewhere. Dismissing a leg (X) runs `onClose` — by convention the
 * origin page's Finish step, same as the Jobs Library and PAIC hand-offs.
 *
 * @param {boolean}  active         Should this leg run (phase matches, page usable).
 * @param {boolean}  ready          Is the page settled enough to measure targets.
 * @param {Array}    steps          Keyed module steps for this leg, in order.
 * @param {Function} resolveTarget  (step) => DOM element for a step.
 * @param {Function} onNext         Advancing past the leg's last step.
 * @param {Function} onClose        Dismissing the leg.
 * @param {Function} [onStepChange] (step, index) — e.g. switch a view so the
 *                                  next step's target is on screen.
 */
const SbTourLeg = ({ active, ready, steps, resolveTarget, onNext, onClose, onStepChange }) => {
  const [current, setCurrent] = useState(0);
  const { open, remountKey } = useReadyTour({ active, ready });

  const { steps: built, actionsRender, indicatorsRender } = buildTourSteps(steps, {
    resolveTarget,
    greeting: NO_GREETING,
  });
  const lastIndex = built.length - 1;
  const legSteps = built.map((step, index) =>
    index === lastIndex ? { ...step, nextButtonProps: { children: "Next" } } : step,
  );

  const changeStep = (index) => {
    onStepChange?.(steps[index], index);
    setCurrent(index);
  };

  // AntD's last-step button calls onClose and THEN onFinish, synchronously —
  // fine for a hand-off that does the same thing either way, but a leg must
  // tell "dismissed" from "advanced". Defer the close a microtask so a Finish
  // that immediately follows can claim it.
  const advancedRef = useRef(false);
  const handleClose = () => {
    advancedRef.current = false;
    queueMicrotask(() => {
      if (!advancedRef.current) onClose();
    });
  };
  const handleFinish = () => {
    advancedRef.current = true;
    onNext();
  };

  return (
    <SbTour
      key={remountKey}
      open={open}
      current={current}
      onChange={changeStep}
      steps={legSteps}
      onClose={handleClose}
      onFinish={handleFinish}
      actionsRender={actionsRender}
      indicatorsRender={indicatorsRender}
    />
  );
};

export default SbTourLeg;
